import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { BoardMotif } from './BoardMotif';
import { VOLUME_PAGES } from '../data/assets';
type Page = {
  src: string;
  alt: string;
  caption: string;
};

/**
 * "Peek inside" — páginas REALES del producto (Volume 1 en Gamma), para que
 * la sales page muestre el pack tal cual está construido. Si alguna no carga,
 * se oculta y queda la esquina de tablero de fondo (sin ícono roto).
 */
const pages: Page[] = [{
  src: VOLUME_PAGES.cover,
  alt: 'Volume 1 cover: The Opening Weeks, over a flat two-color board corner',
  caption: 'Every volume, cover to field card'
}, {
  src: VOLUME_PAGES.session,
  alt: 'Session 1 plan: The Welcome Game, with script, room setup, timing and scoring',
  caption: 'A word-for-word session plan you teach as-is'
}, {
  src: VOLUME_PAGES.worksheet,
  alt: 'Printable worksheet WS-04: The Queen’s Reach Maze',
  caption: 'Printable worksheets for every session'
}, {
  src: VOLUME_PAGES.soNoSoYes,
  alt: 'So-no vs so-yes card: the piece mistakes every beginner makes and the fix line for each',
  caption: '“So no / so yes” — the mistakes and the exact fix'
}, {
  src: VOLUME_PAGES.bigClub,
  alt: 'Big club, few boards: a room plan for 30 kids and 8 sets with a winner-stays rotation',
  caption: 'Big club, few boards: 30 kids, 8 sets'
}, {
  src: VOLUME_PAGES.fieldCard,
  alt: 'Club Starter field card with pre-session checklist, session formula and universal cues',
  caption: 'A field card to keep at the board'
}];
export function LookInside() {
  const trackRef = useRef<HTMLUListElement>(null);
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(true);
  const updateArrows = useCallback(() => {
    const el = trackRef.current;
    if (!el) return;
    setCanPrev(el.scrollLeft > 4);
    setCanNext(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  }, []);
  useEffect(() => {
    updateArrows();
    window.addEventListener('resize', updateArrows);
    return () => window.removeEventListener('resize', updateArrows);
  }, [updateArrows]);
  const scrollByPage = (dir: 1 | -1) => {
    const el = trackRef.current;
    if (!el) return;
    const item = el.querySelector('li');
    const step = item ? item.getBoundingClientRect().width + 16 : 240;
    const visible = Math.max(1, Math.floor(el.clientWidth / step));
    el.scrollBy({
      left: dir * step * visible,
      behavior: 'smooth'
    });
  };
  return <section aria-labelledby="peek-heading" className="w-full bg-ice" data-id="element-137" data-mp="pr6l0rv3vk4c">
      <div className="max-w-6xl mx-auto px-5 pt-16 sm:pt-20" data-id="element-138" data-mp="tpw32xr39pub">
        <div className="text-center" data-id="element-139" data-mp="yqa4hwy6q1jd">
          <p className="text-[11px] font-bold uppercase tracking-[1.6px] text-gold-deep" data-id="element-140" data-mp="yrbst347okj1">
            A peek inside
          </p>
          <h2 id="peek-heading" className="chalk-underline mt-3 font-display text-ink text-[28px] sm:text-5xl" data-id="element-141" data-mp="2586z5t6vtcfg">
            Real pages from the pack
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-base text-ink-soft" data-id="element-142" data-mp="2kaoyh8lp8uv9">
            Not blank templates — every page is diagrammed, scripted and ready
            to print. Here’s a look at Volume 1.
          </p>
        </div>

        <div className="relative mt-9" data-id="element-143" data-mp="26hza4xip7duz">
          <div className="pointer-events-none absolute inset-x-0 top-0 bottom-[4.5rem] z-10 flex items-center justify-between sm:-inset-x-3" data-id="element-144" data-mp="26gxxiom1ajb5">
            <ArrowButton direction="prev" disabled={!canPrev} onClick={() => scrollByPage(-1)} data-id="element-145" data-mp="1wj9u7aad43hd" />
            <ArrowButton direction="next" disabled={!canNext} onClick={() => scrollByPage(1)} data-id="element-146" data-mp="1rkp1dvxfwm2y" />
          </div>
          <ul ref={trackRef} onScroll={updateArrows} className="-mx-5 flex snap-x snap-mandatory gap-4 overflow-x-auto scroll-smooth px-5 pb-5 [scrollbar-width:thin]" data-id="element-147" data-mp="3lypyab9fiz0e">
            {pages.map(page => <li key={page.src} className="snap-start shrink-0 basis-[200px] sm:basis-[232px]" data-id="element-148" data-mp="3oxq52yj1z151">
                <figure className="overflow-hidden rounded-2xl border border-ice-dark bg-white shadow-card" data-id="element-149" data-mp="3fznxu18q7c9b">
                  <div className="relative flex aspect-[9/16] w-full items-center justify-center overflow-hidden bg-cream" data-id="element-150" data-mp="3jzyatxuxr2xo">
                    <BoardMotif className="pointer-events-none absolute h-24 w-24 text-ink/10" data-id="element-151" data-mp="367qlgr5zee90" />
                    <img src={page.src} alt={page.alt} loading="lazy" onError={e => {
                  e.currentTarget.style.display = 'none';
                }} className="absolute inset-0 h-full w-full object-cover object-top" data-id="element-152" data-mp="3b1mf5hf7peyd" />
                  </div>
                  <figcaption className="px-3.5 py-3 text-[12.5px] font-medium leading-snug text-ink" data-id="element-153" data-mp="2s90mf5zlq4i0">
                    {page.caption}
                  </figcaption>
                </figure>
              </li>)}
          </ul>
          <p className="chalk-note mt-1 text-center text-base sm:text-lg text-ink-soft" data-id="element-154" data-mp="2hogg61rvylxq">
            71 sessions · 69 worksheets · 6 field cards across the six volumes
          </p>
        </div>
      </div>
    </section>;
}
function ArrowButton({
  direction,
  disabled,
  onClick
}: {
  direction: 'prev' | 'next';
  disabled: boolean;
  onClick: () => void;
}) {
  const Icon = direction === 'prev' ? ChevronLeftIcon : ChevronRightIcon;
  return <button type="button" onClick={onClick} disabled={disabled} aria-label={direction === 'prev' ? 'Previous pages' : 'Next pages'} className="pointer-events-auto flex h-11 w-11 items-center justify-center rounded-full border border-ice-dark bg-white text-ink shadow-card transition-[opacity,transform,background-color] duration-150 ease-out hover:bg-cream active:scale-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold-deep disabled:pointer-events-none disabled:opacity-0" data-id="element-155" data-mp="31b4del37d6yu">
      <Icon className="h-5 w-5" aria-hidden="true" data-id="element-156" data-mp="27xld460jn1o0" />
    </button>;
}